import { useUser } from "@/composables/user";
import { createMyItemDetailLink, router } from "@/router";
import { saveAfterAuthRedirect } from "@/services/afterAuthRedirectService";
import { catch401 } from "@/services/apiClient";
import type { CollectibleId } from "@/services/collectibles/collectiblesTypes";
import { type CollectibleWithPrice, type Collectible } from "@/services/collectibles/collectiblesTypes";
import type { CollectibleElastic } from "@/services/collectibles/elastic";
import type { CategoryId } from "@/services/collection/categories";
import { type CollectionItem, createCollectionItem, type CreateCollectionItemData } from "@/services/collection/item";
import { Event, EventBus } from "@/services/eventBus";
import { factoryImage, type Image } from "@/services/images";
import { gettext } from "@/services/translation";

export const getCollectibleImages = (collectible: Collectible): Image[] => {
  return collectible.images.map((imageData) => factoryImage(0, imageData.imageUrl, collectible.name));
};

export const hasPrice = (collectible: Collectible): collectible is CollectibleWithPrice => collectible.price !== null;

export const addIntoCollection = async (collectibleId: CollectibleId, categoryId: CategoryId, redirectToNewlyCreatedItem = true): Promise<CollectionItem | void> => {
  const { user } = useUser();
  if (user.value === undefined) {
    const currentLocation = window.location.pathname;
    saveAfterAuthRedirect(currentLocation);
    EventBus.emit({ event: Event.OpenAuthDialog });
  } else {
    const params: CreateCollectionItemData = {
      categoryId,
      collectibleId,
      set: collectibleId,
      minifigure: collectibleId,
      userCollectionListName: "",
    };
    return createCollectionItem(params)
      .then((item: CollectionItem) => {
        const msg = gettext.$gettext("Předmět vytvořen.");
        EventBus.emit({ event: Event.ToastSuccess, data: msg });
        if (redirectToNewlyCreatedItem) {
          router.push(createMyItemDetailLink(item));
        }
        return item;
      })
      .catch(catch401)
      .catch((e: unknown): void => {
        // @todo better error recognization would be nice
        const msg = gettext.$gettext("Předmět se nepodařilo vytvořit.");
        EventBus.emit({ event: Event.ToastError, data: msg });
        throw e;
      });
  }
};

/** @deprecated Rework into elastic collectible and its parameters needed.  */
export const getWidgetIdentificator = (collectible: Collectible): string | undefined => {
  const param = collectible.parameters.find((parameter) => "isWidgetIdentificator" in parameter && parameter.isWidgetIdentificator === true);
  return param?.option.value;
};

export const addCollectionItemToCollectible = (collectible: CollectibleElastic, collectionItem: CollectionItem): void => {
  if (collectible.ownedCount === undefined) {
    collectible.ownedCount = collectionItem.pieces;
  } else {
    collectible.ownedCount += collectionItem.pieces;
  }
};

export const roundSportsCardPrice = (price: number): number => {
  if (price <= 100) return Math.ceil(price);
  if (price <= 500) return Math.ceil(price / 20) * 20;
  if (price <= 2000) return Math.ceil(price / 50) * 50;
  if (price <= 10_000) return Math.ceil(price / 100) * 100;
  return Math.ceil(price / 500) * 500;
};
